import React, { useRef, useState } from "react";
import Cookies from "js-cookie";
import StickyHeader from "../../../components/ModuleCommon/StickyHeader/StickyHeader";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { EQUIPMENT_TYPES } from "./mockData";
import { apiErrorMessage } from "./mappers";

const emptyForm = {
  legalName: "",
  dba: "",
  mc: "",
  dot: "",
  cityState: "",
  yearEstablished: "",
  fleetSize: "",
  hazmat: "",
  equipment: [],
  contactName: "",
  contactRole: "",
  contactPhone: "",
  contactEmail: "",
  laneOrigin: "",
  laneDestination: "",
  notes: "",
};

const blank = (value) => {
  if (value == null) return "";
  const s = String(value).trim();
  return s === "—" ? "" : s;
};

/** Map a loaded carrier profile into the shared create/edit form shape. */
export function carrierToEditForm(carrier) {
  if (!carrier) return { ...emptyForm };
  return {
    ...emptyForm,
    legalName: blank(carrier.name),
    dba: blank(carrier.dba),
    mc: blank(carrier.mc),
    dot: blank(carrier.dot),
    cityState: blank(carrier.cityState),
    fleetSize:
      carrier.fleetSize != null && carrier.fleetSize !== ""
        ? String(carrier.fleetSize)
        : "",
    equipment: Array.isArray(carrier.equipment) ? [...carrier.equipment] : [],
    contactName: blank(carrier.contact?.name),
    contactRole: blank(carrier.contact?.role),
    contactPhone: blank(carrier.contact?.phone),
    contactEmail: blank(carrier.contact?.email),
    notes: carrier.notes || "",
  };
}

const AddNewCarrier = ({
  onCancel,
  onSave,
  mode = "create",
  initialValues = null,
}) => {
  const isEdit = mode === "edit";
  const officeCode = Cookies.get("officeCode") || "-";
  const headerRef = useRef(null);
  const [isTemplateSaved, setIsTemplateSaved] = useState(false);
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    ...(initialValues || {}),
  }));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [infoData, setInfoData] = useState({
    "Legal name": {
      type: "text",
      value: initialValues?.legalName || "",
      readonly: false,
    },
    "MC Number": {
      type: "text",
      value: initialValues?.mc || "",
      readonly: false,
    },
    "DOT Number": {
      type: "text",
      value: initialValues?.dot || "",
      readonly: false,
    },
    Stage: {
      type: "dropdown",
      value: isEdit ? "Editing" : "Prospecting",
      options: isEdit
        ? ["Editing"]
        : ["Prospecting"],
      readonly: isEdit,
    },
  });

  const templates = ["Default Template"];

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleInfoChange = (field, newValue) => {
    setInfoData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value: newValue,
      },
    }));

    if (field === "Legal name") {
      setForm((prev) => ({ ...prev, legalName: newValue }));
    }
    if (field === "MC Number") {
      setForm((prev) => ({ ...prev, mc: newValue }));
    }
    if (field === "DOT Number") {
      setForm((prev) => ({ ...prev, dot: newValue }));
    }
  };

  const toggleEquipment = (eq) => {
    setForm((prev) => {
      const has = prev.equipment.includes(eq);
      return {
        ...prev,
        equipment: has
          ? prev.equipment.filter((x) => x !== eq)
          : [...prev.equipment, eq],
      };
    });
  };

  const handleSave = async () => {
    const legalName = (form.legalName || infoData["Legal name"]?.value || "").trim();
    const mc = (form.mc || infoData["MC Number"]?.value || "").trim();
    const dot = (form.dot || infoData["DOT Number"]?.value || "").trim();

    if (!legalName) {
      setError("Legal company name is required.");
      return;
    }
    if (!mc && !dot) {
      setError("Provide at least an MC or DOT number.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await onSave({
        ...form,
        legalName,
        mc,
        dot,
      });
    } catch (err) {
      setError(apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const buttons = isEdit
    ? [
        {
          label: saving ? "Saving…" : "Save Changes",
          type: "primary",
          onClick: handleSave,
        },
        { label: "Cancel", type: "secondary", onClick: onCancel },
      ]
    : [
        {
          label: saving ? "Saving…" : "Save Carrier",
          type: "primary",
          onClick: handleSave,
        },
        { label: "Cancel", type: "secondary", onClick: onCancel },
      ];

  if (saving) {
    return (
      <div className="w-full h-full min-h-0 flex flex-col">
        <Loader message={isEdit ? "Saving carrier..." : "Creating carrier..."} />
      </div>
    );
  }

  return (
    <div className="w-full h-full min-h-0 flex flex-col">
      <div ref={headerRef} className="sticky top-0 z-10 bg-white shadow-md w-full">
        <StickyHeader
          infoData={infoData}
          onInfoChange={handleInfoChange}
          templates={templates}
          onSelectTemplate={() => {}}
          buttons={buttons}
          saveTemplateConfig={{
            checked: isTemplateSaved,
            onChange: () => setIsTemplateSaved((prev) => !prev),
          }}
        />
      </div>

      <div className="flex-1 overflow-y-auto bg-white w-full p-fluid-1">
        <div className="bg-white rounded-[clamp(0.75rem,1vw,1.5rem)] shadow-fluid-card p-fluid-2">
          <h2 className="text-fluid-3xl font-bold mb-fluid-2">
            {isEdit ? "Edit Carrier" : "Add Carrier"}{" "}
            <span className="text-fluid-base font-normal text-gray-500">
              - {officeCode}
            </span>
          </h2>

          {error ? (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-fluid-sm text-red-700">
              {error}
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div>
              <label className="block font-medium mb-1">Legal Company Name</label>
              <input
                type="text"
                className="border rounded-md p-2 w-full"
                value={form.legalName}
                onChange={(e) => {
                  set("legalName", e.target.value);
                  handleInfoChange("Legal name", e.target.value);
                }}
                placeholder="e.g. Blue Ridge Freight LLC"
              />
            </div>
            <div>
              <label className="block font-medium mb-1">DBA (if different)</label>
              <input
                type="text"
                className="border rounded-md p-2 w-full"
                value={form.dba}
                onChange={(e) => set("dba", e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block font-medium mb-1">City, State</label>
              <input
                type="text"
                className="border rounded-md p-2 w-full"
                value={form.cityState}
                onChange={(e) => set("cityState", e.target.value)}
                placeholder="e.g. Charlotte, NC"
              />
            </div>
            <div>
              <label className="block font-medium mb-1">MC Number</label>
              <input
                type="text"
                className="border rounded-md p-2 w-full"
                value={form.mc}
                onChange={(e) => {
                  set("mc", e.target.value);
                  handleInfoChange("MC Number", e.target.value);
                }}
                placeholder="MC-XXXXXX"
              />
            </div>
            <div>
              <label className="block font-medium mb-1">DOT Number</label>
              <input
                type="text"
                className="border rounded-md p-2 w-full"
                value={form.dot}
                onChange={(e) => {
                  set("dot", e.target.value);
                  handleInfoChange("DOT Number", e.target.value);
                }}
                placeholder="DOT-XXXXXXX"
              />
            </div>
            {!isEdit ? (
              <div>
                <label className="block font-medium mb-1">Year Established</label>
                <input
                  type="text"
                  className="border rounded-md p-2 w-full"
                  value={form.yearEstablished}
                  onChange={(e) => set("yearEstablished", e.target.value)}
                  placeholder="e.g. 2014"
                />
              </div>
            ) : null}
            <div>
              <label className="block font-medium mb-1">Fleet Size (tractors)</label>
              <input
                type="number"
                className="border rounded-md p-2 w-full"
                value={form.fleetSize}
                onChange={(e) => set("fleetSize", e.target.value)}
                placeholder="e.g. 62"
              />
            </div>
            {!isEdit ? (
              <div>
                <label className="block font-medium mb-1">Hazmat Capable?</label>
                <select
                  className="border rounded-md p-2 w-full"
                  value={form.hazmat}
                  onChange={(e) => set("hazmat", e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            ) : null}
            <div className="md:col-span-3">
              <label className="block font-medium mb-1">Equipment Types</label>
              <div className="mt-2 flex flex-wrap gap-3">
                {EQUIPMENT_TYPES.map((eq) => (
                  <label key={eq} className="flex items-center gap-2 text-fluid-sm">
                    <input
                      type="checkbox"
                      checked={form.equipment.includes(eq)}
                      onChange={() => toggleEquipment(eq)}
                    />
                    {eq}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block font-medium mb-1">Contact Name</label>
              <input
                type="text"
                className="border rounded-md p-2 w-full"
                value={form.contactName}
                onChange={(e) => set("contactName", e.target.value)}
              />
            </div>
            <div>
              <label className="block font-medium mb-1">Role</label>
              <input
                type="text"
                className="border rounded-md p-2 w-full"
                value={form.contactRole}
                onChange={(e) => set("contactRole", e.target.value)}
              />
            </div>
            <div>
              <label className="block font-medium mb-1">Phone</label>
              <input
                type="text"
                className="border rounded-md p-2 w-full"
                value={form.contactPhone}
                onChange={(e) => set("contactPhone", e.target.value)}
              />
            </div>
            <div>
              <label className="block font-medium mb-1">Email</label>
              <input
                type="text"
                className="border rounded-md p-2 w-full"
                value={form.contactEmail}
                onChange={(e) => set("contactEmail", e.target.value)}
              />
            </div>
            {!isEdit ? (
              <>
                <div>
                  <label className="block font-medium mb-1">Origin</label>
                  <input
                    type="text"
                    className="border rounded-md p-2 w-full"
                    value={form.laneOrigin}
                    onChange={(e) => set("laneOrigin", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Destination</label>
                  <input
                    type="text"
                    className="border rounded-md p-2 w-full"
                    value={form.laneDestination}
                    onChange={(e) => set("laneDestination", e.target.value)}
                  />
                </div>
              </>
            ) : null}
            <div className="md:col-span-3">
              <label className="block font-medium mb-1">Notes</label>
              <textarea
                rows={3}
                className="border rounded-md p-2 w-full"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewCarrier;
